/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react'
import {
    Text,
    Flex,
    Stack,
    Heading,
    useStyleConfig,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import RecommendedProducts from '../../../components/recommended-products'
import {useSiteContext} from '../../../contexts/site'

const EmptySearchResults = ({searchQuery, openQuickAddModal = false}) => {
    const {
        app: {commerceAPI}
    } = getConfig()
    const {isYR} = useSiteContext()
    const {noSearchResultPageRecommenderIds} = commerceAPI.storefront
    let recommenderId = ''
    try {
        if (noSearchResultPageRecommenderIds) {
            recommenderId = JSON.parse(noSearchResultPageRecommenderIds)?.[isYR ? 'yr' : 'ks']
        }
    } catch (e) {
        console.log(e.message)
    }
    const productListStyles = useStyleConfig('ProductListStyles')
    const intl = useIntl()
    const emprtCartProps = openQuickAddModal
        ? {maxWidth: '100%', padding: {base: '0 15px', lg: '0px 100px 0px 100px'}}
        : {}
    return (
        <Flex
            data-testid={`sf-${openQuickAddModal ? 'empty-cart' : 'product-empty-list'}-page`}
            {...productListStyles.noSrp.page}
            {...emprtCartProps}
        >
            <Fragment>
                {!openQuickAddModal && (
                    <Heading as="h1" {...productListStyles.noSrp.heading}>
                        {!searchQuery
                            ? intl.formatMessage({
                                  id: 'page.not.found',
                                  defaultMessage: 'Page Not Found'
                              })
                            : intl.formatMessage(
                                  {
                                      id: 'no.search.results.for',
                                      defaultMessage: 'No Search Results for "{searchQuery}"'
                                  },
                                  {
                                      searchQuery: searchQuery
                                  }
                              )}
                    </Heading>
                )}
                {!openQuickAddModal && (
                    <Text {...productListStyles.noSrp.description}>
                        {!searchQuery
                            ? intl.formatMessage({
                                  id: 'no.search.results.invalid.catgeory',
                                  defaultMessage:
                                      "We're sorry! We seem to have misplaced this page. Please try the search function below to find what you were looking for."
                              })
                            : intl.formatMessage({
                                  id: 'no.search.spelling.check',
                                  defaultMessage:
                                      "We're sorry, no products were found for your search. Try again using a different spelling or keywords."
                              })}
                    </Text>
                )}
                {!openQuickAddModal && !searchQuery && <Box className="mt-not-found-recs"></Box>}
                {(openQuickAddModal || searchQuery) && (
                    <Stack
                        spacing="20px"
                        className="empty-recommendations-wrapper"
                        margin={{
                            base: openQuickAddModal ? '50px 0px' : '35px 0px',
                            lg: openQuickAddModal ? '80px 0px' : '35px 0px'
                        }}
                        maxWidth="-webkit-fill-available"
                        width="100%"
                        sx={{
                            '.list-title': {
                                fontSize: '25px',
                                fontWeight: '400',
                                marginBottom: '15px',
                                fontFamily: 'heading',
                                color: 'black.100',
                                padding: 0,
                                letterSpacing: '0.125px'
                            },
                            '.spinner img': {
                                width: '45px',
                                height: '45px'
                            },
                            'div[data-testid="product-scroller"]': {
                                paddingY: '0px'
                            },
                            img: {
                                width: '119px',
                                height: '149px'
                            },
                            '.swiper-slide': {
                                width: '119px !important',
                                marginRight: '0px !important'
                            },
                            'div[role="radiogroup"]': {
                                display: 'none'
                            },
                            '.info-wrapper': {
                                margin: '6.58px 0 6.58px 0'
                            },
                            '.quick-add-parent': {
                                marginInline: 0
                            },
                            '.display-price-wrapper': {
                                marginRight: '6.58px',
                                marginBottom: '6.58px'
                            },
                            '.product-name': {
                                margin: '0px 6.58px 6.58px 0',
                                fontSize: '15px',
                                minHeight: '45px',
                                letterSpacing: '0.075px'
                            },
                            '.info-wrapper b': {
                                fontSize: '13px',
                                letterSpacing: '0.065px'
                            },
                            '.swiper-wrapper': {
                                paddingLeft: '0 !important',
                                gap: '20px'
                            },
                            '.swiper': {
                                padding: '0px !important',
                                margin: '0 !important',
                                position: 'static'
                            },
                            '.swiper-parent': {
                                width: '100%',
                                border: '1px solid',
                                borderRadius: '10px',
                                padding: '20px',
                                borderColor: 'gray.400'
                            },
                            '.swiper-button-next, .swiper-button-prev': {
                                top: '0 !important',
                                opacity: '1 !important',
                                display: 'block !important',
                                boxShadow: 'none !important',
                                backgroundColor: 'transparent !important',
                                '&.swiper-button-disabled': {
                                    opacity: '0.35 !important',
                                    display: 'block !important'
                                }
                            },
                            '.swiper-button-next': {
                                right: '-10px !important',
                                left: 'initial !important'
                            },
                            '.swiper-button-prev': {
                                right: '37px !important',
                                left: 'initial !important'
                            },
                            '.tiles-skeleton-wrapper': {
                                marginLeft: '0px',
                                gap: '20px',
                                marginTop: '0px'
                            },
                            '.wishlist-wrapper': {
                                display: 'none !important'
                            },
                            '.skeleton-tile-name': {
                                width: '100px',
                                height: '22px'
                            },
                            '.skeleton-tile-price': {
                                height: '20px'
                            },
                            '.title-skeleton': {
                                margin: '0 0 15px 0',
                                height: '30px',
                                width: '205px'
                            }
                        }}
                    >
                        <RecommendedProducts
                            hideArrows={false}
                            itemWidth="119px"
                            quickAddLabel={false}
                            openQuickAddModal={openQuickAddModal}
                            showImageSlider={true}
                            recommender={recommenderId}
                            mx={0}
                        />
                    </Stack>
                )}
            </Fragment>
        </Flex>
    )
}

EmptySearchResults.propTypes = {
    searchQuery: PropTypes.string,
    openQuickAddModal: PropTypes.bool
}

export default EmptySearchResults
